var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { get, each, isEmpty } from "lodash";
import authentication from "@irati/auth";
import productsErrorHandler from "@irati/deals/services/errors/productsErrorHandler";
import outstandingBalance from "@irati/payments/helpers/outstandingBalance";
import { PaymentStatus } from "@irati/payments/enums/PaymentV2";
import { getPaymentOrder, payByUserWallet } from "@irati/payments/services/Payments";
import perksSagaService from "@irati/deals/services/perks-saga";
import { BasketItemType } from "@irati/types";
import basket from "@irati/checkout/utils/basket";
import SagaSteps from "../../constants/sagaSteps";
import { isSingleProductOnly } from "./deal";
var ABORTED = "ABORTED";
function getSaga(uuid, formatMessage) {
    return perksSagaService.get(uuid, { forceRefetch: true }).then(function (saga) {
        return checkForSagaError(saga, formatMessage);
    });
}
function checkForSagaError(saga, formatMessage) {
    if (saga.errorCode) {
        var e = new Error();
        e.errorCode = saga.errorCode;
        e.errorData = saga.errorData;
        e.message = productsErrorHandler(e.errorCode);
        throw e;
    }
    if (!isEmpty(saga.errors)) {
        throw new Error(saga.errors.join(", "));
    }
    if (saga.status === ABORTED) {
        throw new Error(formatMessage({ id: "perks.helpers.redemptionUtils.error.cancelled" }));
    }
    return saga;
}
function nextStep(saga, deal, details) {
    if (details === void 0) { details = {}; }
    if (!saga) {
        /**
         * No saga = new redemption
         */
        var user = authentication.user();
        return perksSagaService.start(deal, user).then(function (createdSaga) {
            var products = get(createdSaga, "redemption.fullDeal.productConfig.products", []);
            if (createdSaga && !isEmpty(products) && isSingleProductOnly(deal)) {
                return handleSingleProductOnly(createdSaga);
            }
            return createdSaga;
        });
    }
    switch (saga.nextStep) {
        case SagaSteps.ORDER: {
            var products = basket.items.map(function (item) {
                return {
                    uuid: item.productId,
                    openValue: item.openValue,
                    quantity: item.count
                };
            });
            if (products.length === 0) {
                basket.setProduct(BasketItemType.Deal, deal.uuid, details[0].uuid, { count: details[0].quantity });
                products = details;
            }
            return perksSagaService.order(saga, { products: products }).then(checkForSagaError);
        }
        case SagaSteps.COMPLETE: {
            return perksSagaService.complete(saga).then(checkForSagaError);
        }
        case SagaSteps.UPDATE: {
            return perksSagaService.update(saga, details).then(checkForSagaError);
        }
        case SagaSteps.PAY: {
            return perksSagaService.pay(saga, details).then(checkForSagaError);
        }
        default: {
            return getSaga(saga.uuid);
        }
    }
}
/**
 * check saga next step against SagaSteps, return next path
 * @param saga
 * @param deal
 * @returns {string}
 */
var currentStep = function (saga, deal) {
    if (!saga.nextStep) {
        return "/info/" + deal.urlSlug + "/success/" + saga.uuid;
    }
    if (saga.nextStep === SagaSteps.ORDER) {
        return "/info/" + deal.urlSlug + "/redeem/" + saga.uuid;
    }
    if (saga.nextStep === SagaSteps.UPDATE && deal.emailRequired) {
        return "/info/" + deal.urlSlug + "/redeem/update/" + saga.uuid;
    }
    if (saga.nextStep === SagaSteps.PAY) {
        return "/info/" + deal.urlSlug + "/pay/" + saga.uuid;
    }
    return "/";
};
var makeWalletPaymentIfRequired = function (saga) {
    return getPaymentOrder(saga.paymentOrderId, { forceRefetch: true }).then(function (paymentOrder) {
        var outstanding = outstandingBalance(paymentOrder, paymentOrder.amount);
        if (outstanding === 0) {
            return Promise.resolve(saga);
        }
        if (paymentOrder.payments.some(function (payment) { return payment.status === PaymentStatus.Failed; })) {
            return Promise.reject();
        }
        var redemption = saga.redemption, redemptionId = saga.redemptionId, uuid = saga.uuid;
        return payByUserWallet(paymentOrder, { amount: outstanding }, redemption.deal, redemptionId).then(function () { return getSaga(uuid); });
    });
};
var complete = function (saga) {
    return makeWalletPaymentIfRequired(saga).then(function (updatedSaga) {
        return nextStep(updatedSaga, updatedSaga.redemption.fullDeal).then(function (completedSaga) {
            return completedSaga;
        });
    });
};
/**
 * Check stock by ids,
 * data returned {object} - {stock totals}, [out of stock prods], (bool)allProductsOutOfStock
 * @param uuids
 * @returns {Promise.<TResult>}
 */
function checkStock(dealId) {
    return perksSagaService.stockCount(dealId).then(function (_a) {
        var stockTotals = _a.stockTotals, allOutOfStock = _a.allOutOfStock;
        var outOfStock = [];
        each(stockTotals, function (count, id) {
            if (count === 0) {
                outOfStock.push(id);
            }
        });
        return {
            allProductsOutOfStock: allOutOfStock,
            outOfStock: outOfStock,
            stock: stockTotals
        };
    });
}
/**
 * Current behaviour is to fetch saga, deal
 * will redirect to correct step in future when continuation is possible
 */
function handleIncorrectStep(sagaId) {
    return perksSagaService.get(sagaId, { forceRefetch: false }).then(function (saga) {
        return __assign(__assign({}, saga), { deal: saga.redemption.fullDeal });
    });
}
function handleSagaErrors(err) {
    var errorCode = err.errorCode;
    var error = errorCode ? err : get(err, "message", err);
    if (error.response) {
        error = get(err, "response.data.message", err);
    }
    return error;
}
function handleSingleProductOnly(saga) {
    var deal = saga.redemption.fullDeal;
    var products = saga.redemption.fullDeal.productConfig.products;
    return perksSagaService.stockCount(deal.uuid).then(function (_a) {
        var allOutOfStock = _a.allOutOfStock;
        if (allOutOfStock) {
            throw new Error("perks.containers.DealImportantInfoContainer.noStock.error");
        }
        var product = products[0];
        var details = [
            {
                uuid: product.uuid,
                quantity: 1
            }
        ];
        return nextStep(saga, deal, details);
    });
}
export default {
    nextStep: nextStep,
    currentStep: currentStep,
    checkStock: checkStock,
    getSaga: getSaga,
    handleIncorrectStep: handleIncorrectStep,
    handleSagaErrors: handleSagaErrors,
    complete: complete
};
